
import { useState } from "react";
import { PatientFileList } from "./PatientFileList";
import { AddPatientFileDialog } from "./AddPatientFileDialog";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, FileText, FileCog, Download, BarChart, Users, Settings } from "lucide-react";
import { usePatientFileExport } from "./usePatientFileExport";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const PatientFileManagement = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { exportToPDF, exportToCSV } = usePatientFileExport();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  
  // Get a single file for export demonstration
  const { data: allFiles, refetch: refetchFiles } = useQuery({
    queryKey: ["patient-files-for-export"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_files")
        .select("*")
        .limit(1);

      if (error) throw error;
      return data;
    },
  });
  
  // Statistiques des dossiers
  const { data: fileStats } = useQuery({
    queryKey: ["patient-files-stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("Utilisateur non authentifié");
      
      const { count: totalFiles, error: totalError } = await supabase
        .from("patient_files")
        .select("*", { count: 'exact', head: true })
        .eq("professional_id", user.id);
        
      const { count: recentFiles, error: recentError } = await supabase
        .from("patient_files")
        .select("*", { count: 'exact', head: true })
        .eq("professional_id", user.id)
        .gte("created_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());
      
      if (totalError || recentError) throw totalError || recentError;
      
      return {
        total: totalFiles || 0,
        recent: recentFiles || 0
      };
    }
  });

  // Handle successful patient file addition
  const handlePatientFileAdded = () => {
    refetchFiles();
  };

  return (
    <Card className="border shadow-md">
      <CardHeader className="pb-4 border-b bg-muted/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-xl font-bold text-primary">Gestion des dossiers patients</CardTitle>
            <CardDescription className="mt-1">
              Créez, consultez et gérez les dossiers médicaux de vos patients
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-1">
                  <Download className="h-4 w-4" />
                  <span>Exporter</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Format d'export</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => allFiles?.[0] && exportToCSV(allFiles[0])}>
                  Exporter en CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => allFiles?.[0] && exportToPDF(allFiles[0], 'standard')}>
                  Exporter en PDF (Standard)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => allFiles?.[0] && exportToPDF(allFiles[0], 'detailed')}>
                  Exporter en PDF (Détaillé)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => allFiles?.[0] && exportToPDF(allFiles[0], 'compact')}>
                  Exporter en PDF (Compact)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              onClick={() => setIsAddDialogOpen(true)}
              size="sm"
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span>Nouveau dossier</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {fileStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Dossiers</p>
                  <p className="text-2xl font-bold">{fileStats.total}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-600 font-medium">Dossiers récents</p>
                  <p className="text-2xl font-bold">{fileStats.recent}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200 md:col-span-1 col-span-2">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="bg-amber-100 p-2 rounded-full">
                  <BarChart className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-amber-600 font-medium">Taux de complétion</p>
                  <p className="text-2xl font-bold">85%</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="files" className="w-full">
          <TabsList className="mb-4 bg-muted/40 p-1">
            <TabsTrigger value="files" className="flex items-center gap-1 data-[state=active]:bg-white">
              <FileText className="h-4 w-4" />
              Dossiers
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1 data-[state=active]:bg-white">
              <Settings className="h-4 w-4" />
              Paramètres
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="files" className="space-y-6">
            <PatientFileList />
          </TabsContent>
          
          <TabsContent value="settings" className="space-y-6">
            <Card className="border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Paramètres des dossiers patients</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6">
                  <div className="rounded-md border p-4 bg-card shadow-sm">
                    <h3 className="font-medium mb-2 text-primary flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Génération de numéros de dossier
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Les numéros de dossiers sont générés automatiquement suivant le format YYYY-00001, où YYYY représente l'année en cours.
                    </p>
                  </div>
                  
                  <div className="rounded-md border p-4 bg-card shadow-sm">
                    <h3 className="font-medium mb-2 text-primary flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Export de dossiers
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Plusieurs options d'export sont disponibles : CSV et PDF (standard, détaillé ou compact).
                      Les exports PDF incluent toutes les informations du patient ainsi que l'historique des consultations.
                    </p>
                  </div>
                  
                  <div className="rounded-md border p-4 bg-card shadow-sm">
                    <h3 className="font-medium mb-2 text-primary flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Accès aux dossiers
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Seuls les professionnels ayant créé les dossiers peuvent y accéder. Un système de partage entre professionnels sera disponible prochainement.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <AddPatientFileDialog 
          open={isAddDialogOpen} 
          onOpenChange={setIsAddDialogOpen}
          onSuccess={handlePatientFileAdded}
        />
      </CardContent>
    </Card>
  );
};
